import React, { useMemo, useState, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { allTimezones, fmtTime, tzLabel, zonedParts } from '../../lib/timezone';

const dayLabel = (iso, tz) => new Date(iso).toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' });

export const TimezoneSelect = ({ value, onChange, testId = 'timezone-select', className = '' }) => (
  <label className={`flex items-center gap-2 text-sm ${className}`}>
    <Globe size={15} className="text-slate-500 shrink-0" />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm w-full min-w-0 sm:min-w-[300px] max-w-md"
      data-testid={testId}
    >
      {allTimezones().map((z) => <option key={z} value={z}>{tzLabel(z)} — {z}</option>)}
    </select>
  </label>
);

export const SlotPicker = ({ slots, tz, onTzChange, selected, onSelect }) => {
  const days = useMemo(() => {
    const map = {};
    (slots || []).forEach((s) => {
      const k = zonedParts(s.starts_at, tz).key;
      (map[k] = map[k] || []).push(s);
    });
    return Object.keys(map).sort().map((k) => ({ key: k, slots: map[k] }));
  }, [slots, tz]);
  const [dayKey, setDayKey] = useState(null);
  useEffect(() => {
    if (!days.find((d) => d.key === dayKey)) setDayKey(days[0]?.key || null);
  }, [days]); // eslint-disable-line
  const active = days.find((d) => d.key === dayKey);

  return (
    <div className="space-y-4" data-testid="slot-picker">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">Times shown in <b data-testid="slot-tz-label">{tzLabel(tz, active ? new Date(active.slots[0].starts_at) : new Date())}</b></p>
        <TimezoneSelect value={tz} onChange={onTzChange} />
      </div>
      {days.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center" data-testid="no-slots">No open times right now. Please check back later.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
          <div className="flex sm:flex-col gap-1.5 overflow-x-auto sm:overflow-y-auto sm:max-h-[360px] pb-1">
            {days.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setDayKey(d.key)}
                className={`shrink-0 text-left px-3 py-2 rounded-md border text-sm transition-colors ${d.key === dayKey ? 'bg-[#1a3a5c] text-white border-[#1a3a5c]' : 'bg-white hover:border-[#1a3a5c]/50 border-slate-200'}`}
                data-testid={`slot-day-${d.key}`}
              >
                <div className="font-medium">{dayLabel(d.slots[0].starts_at, tz)}</div>
                <div className={`text-xs ${d.key === dayKey ? 'text-white/80' : 'text-slate-500'}`}>{d.slots.length} times</div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 content-start sm:max-h-[360px] overflow-y-auto">
            {(active?.slots || []).map((s) => (
              <button
                key={s.starts_at}
                type="button"
                onClick={() => onSelect(s)}
                className={`h-11 rounded-md border text-sm font-medium transition-colors ${selected?.starts_at === s.starts_at ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-200 hover:border-emerald-600 hover:text-emerald-700'}`}
                data-testid={`slot-${s.starts_at}`}
              >
                {fmtTime(s.starts_at, tz)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
